import React, { useEffect, useMemo, useState } from 'react';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';

import { Columns, StyledFormContainer, StyledPaperContainer, StyledSectionTitle } from '../../PlusWebsiteEditor.styles';
import ImageUploader from '../../components/ImageUploader/ImageUploader';
import { HealtheraButton, InputField } from '../../../../healtheraComponents';
import { WebsiteConfiguration } from '../../types/websiteConfiguration';
import { BlurHandler, DeepFieldErrorGetter, SetFieldValue } from '../../types/handlers';
import { StyledAddMoreButtonContainer, StyledImagesContainer, ColumnWithGap } from './HeroBlock.styled';

type Props = WebsiteConfiguration['hero'] & {
  onChange: SetFieldValue<string>;
  getError: DeepFieldErrorGetter;
  onBlur: BlurHandler;
  setFieldValue: SetFieldValue<any>;
  setNotification: (notification: string) => void;
  ctaPossibleLinks?: { url: string, title: string }[];
  pharmacy_required?: boolean;
  pharmacy_required_redirect?: string;
};

const HeroBlock = ({
  headline,
  subtitle,
  learn_more_intro,
  cta_text,
  cta_link,
  image_urls,
  images,
  ctaPossibleLinks,
  onChange,
  setFieldValue,
  setNotification,
  onBlur,
  getError,
}: Props) => {
  const [showNewImageUploader, setShowNewImageUploader] = useState(!image_urls?.length);
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.name, e.target.value);
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.name, e.target.value);
  const imagesArray = [
    ...(images ? (image_urls ? (image_urls.length > images.length ? image_urls : images) : images) : image_urls || []),
  ]?.fill('');

  useEffect(() => {
    if (imagesArray.length === 0) {
      setShowNewImageUploader(true);
    }
  }, [imagesArray]);

  const handleChangeImage = (index: number, base64: string) => {
    if (images?.includes(base64)) {
      return setNotification('This image was already uploaded');
    }
    onChange(`images.${index}`, base64, true);
  };

  const handleUploadNewImage = (base64: string) => {
    if (images?.includes(base64)) {
      return setNotification('This image was already uploaded');
    }
    onChange(`images.${imagesArray?.length ? imagesArray.length : 0}`, base64, true);
    setShowNewImageUploader(false);
  };

  const handleRemoveImage = (index: number) => {
    if (images?.[index])
      setFieldValue(
        'hero.images',
        images.filter((item) => item !== images[index])
      );
    if (image_urls?.[index])
      setFieldValue(
        'hero.image_urls',
        image_urls.filter((item) => item !== image_urls[index])
      );
  };

  const ctaLinkValuesToTitle = useMemo(() => {
    if (!ctaPossibleLinks || ctaPossibleLinks.length === 0) return {};

    return ctaPossibleLinks.reduce((acc, link) => {
      acc[link.url] = link.title;
      return acc;
    }, { '': 'Default' });
  }, [ctaPossibleLinks]);

  return (
    <StyledPaperContainer>
      <StyledSectionTitle>Hero block</StyledSectionTitle>
      <Columns>
        <StyledFormContainer>
          <ColumnWithGap>
            <InputField
              name="headline"
              onBlur={onBlur}
              onChange={handleInputChange}
              label="Title"
              value={headline}
              helperText={getError('headline')}
            />
            <InputField
              name="subtitle"
              onBlur={onBlur}
              onChange={handleInputChange}
              label="Subtitle"
              value={subtitle}
              helperText={getError('subtitle')}
              multiline
            />
            <InputField
              name="learn_more_intro"
              onBlur={onBlur}
              onChange={handleInputChange}
              label="Learn more intro"
              value={learn_more_intro}
              helperText={getError('learn_more_intro')}
            />
            <InputField
              name="cta_text"
              onBlur={onBlur}
              onChange={handleInputChange}
              label="CTA button text"
              value={cta_text}
              helperText={getError('cta_text')}
            />
            {ctaPossibleLinks && ctaPossibleLinks?.length > 0 && (
              <FormControl>
                <InputLabel htmlFor="cta-link">CTA navigation</InputLabel>
                <Select
                  value={cta_link}
                  onBlur={onBlur}
                  onChange={handleSelectChange}
                  inputProps={{ name: 'cta_link', id: 'cta-link' }}
                  renderValue={() => ctaLinkValuesToTitle[cta_link || '']}
                  displayEmpty
                >
                  <MenuItem value="">
                    Default
                  </MenuItem>
                  {ctaPossibleLinks.map(link => (
                    <MenuItem key={link.url} value={link.url}>
                      {link.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </ColumnWithGap>
        </StyledFormContainer>
        <StyledImagesContainer>
          {[
            imagesArray?.map((_, index) => (
              <ImageUploader
                key={images?.[index] || image_urls?.[index] || ''}
                helperText={getError('images')}
                title={`Hero Image ${index + 1}`}
                url={images?.[index] || image_urls?.[index] || ''}
                onChange={(base64) => handleChangeImage(index, base64)}
                onRemove={() => handleRemoveImage(index)}
              />
            )),
          ]}
          {showNewImageUploader && (
            <ImageUploader
              title={`Hero Image ${imagesArray?.length ? imagesArray.length + 1 : 0}`}
              url={images?.[imagesArray?.length ? imagesArray.length : 0] || ''}
              onChange={handleUploadNewImage}
              {...(imagesArray?.length > 0 ? { onRemove: () => setShowNewImageUploader(false) } : {})}
            />
          )}
          {!showNewImageUploader && (
            <StyledAddMoreButtonContainer>
              <HealtheraButton
                onClick={() => setShowNewImageUploader(true)}
                size="small"
                height="auto"
                padding="2px 0"
                width="120px"
              >
                Add more
              </HealtheraButton>
            </StyledAddMoreButtonContainer>
          )}
        </StyledImagesContainer>
      </Columns>
    </StyledPaperContainer>
  );
};

export default HeroBlock;
